import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError, lastValueFrom, concatMap, from, tap } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { CookieService } from 'ngx-cookie-service';
import { User } from '../user/user.types';
import { AuthResponse, TokenApi, UserForgot, UserReset } from './auth.types';
import { AppConfigService } from 'app/app-config.service';
import { ResponseUserInfo } from 'app/shared/services/user-info.service';
import { CsrfTokenService } from 'app/shared/services/csrf.service';
@Injectable()
export class AuthService {
    roleName: Array<string> = ['', 'SuperAdmin', 'Admin', 'LineManager', 'User', 'Assessor'];
    userId: string;
    role: string;

    private _authenticated: boolean = false;
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private cookieService: CookieService,
        private configService: AppConfigService,
        private csrfTokenService: CsrfTokenService

    ) {

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        this.cookieService.delete('accessToken');
        this.cookieService.set('accessToken', token, { expires: 1, secure: true, sameSite: 'Strict', path: '/' });
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get accessToken(): string {
        return this.cookieService.get('accessToken') ?? '';
    }

    set refreshToken(token: string) {
        this.cookieService.delete('refreshToken');
        this.cookieService.set('refreshToken', token, { expires: 30, secure: true, sameSite: 'Strict', path: '/' });
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get refreshToken(): string {
        return this.cookieService.get('refreshToken') ?? '';

    }
    clearToken(): void {
        this.cookieService.delete('accessToken');
        this.cookieService.delete('refreshToken');
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotForm(email: string): Observable<any> {

        console.log('forgotPassword');
        const user = {
            email: email
        } as UserForgot;
        return this._httpClient.post<any>(this.configService.getConfiguration().apiUrl + '/Authenticate/Forgot', user)
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    if (response.success) {
                        // Return true
                        return of(true);
                    }
                    else {
                        of(false);
                    }
                })
            );

    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, uid: string): Observable<any> {
        const user = {
            id: uid,
            password: password
        } as UserReset;
        return this._httpClient.post<any>(this.configService.getConfiguration().apiUrl + '/Authenticate/Reset', user)
            .pipe(
                catchError(() =>

                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    if (response.success) {

                        // Return true
                        return of(true);
                    }
                    else {

                        of(false);
                    }

                })
            );
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { employeeTypeId: number; username: string; password: string }): Observable<any> {
        // Clear user data only if needed, here before making the login request
        return this._userService.clearDataUser().pipe(
            switchMap(() => {
                console.log('signIn');
                return this._httpClient.post<AuthResponse>(this.configService.getConfiguration().apiUrl + '/Authenticate/issue/', credentials);
            }),
            catchError(() => of(false)),
            concatMap((response: AuthResponse) => {
                if (response && response.isAuthSuccessful) {
                    this.accessToken = response.token;
                    this.refreshToken = response.refreshToken;
                    this._authenticated = true;

                    const userProfile: User = {
                        id: response.userInfo.userId,
                        username: response.userInfo.userName,
                        name: response.userInfo.employeeName,
                        email: response.userInfo.email,
                        avatar: '',
                        status: '',
                        role: this.roleName[response.userInfo.roleId],
                        unit: response.userInfo.unitCode,
                        type: 'SO'
                    };

                    // Ensure CSRF token is fetched and then set the user
                    return from(this.csrfTokenService.fetchCsrfToken()).pipe(
                        tap(() => this._userService.user = userProfile),
                        switchMap(() => of(response)) // Return the response as an Observable
                        // switchMap(() => {
                        //     this._userService.user = userProfile; // เซ็ต User ทันทีที่ได้ CSRF
                        //     return of(response); // ส่ง response กลับไปให้ component สั่ง navigate
                        // }),
                        // catchError(err => {
                        //     console.error('CSRF Fetch Error:', err);
                        //     return of(response); // ถึง CSRF พัง ก็ยังยอมให้ส่ง response เพื่อให้ login ผ่านไปก่อนได้
                        // })
                    );
                } else {
                    // Clear user data again if authentication fails
                    return this._userService.clearDataUser().pipe(
                        switchMap(() => throwError(() => new Error('Username หรือ Password ไม่ถูกต้อง')))
                    );
                }
            }),
        );
    }



    async signInAD(credentials: { id: string; email: string }): Promise<any> {
        try {
            await lastValueFrom(this._userService.clearDataUser());  // Assuming clearDataUser returns an Observable
            const response = await lastValueFrom(this._httpClient.post<AuthResponse>(this.configService.getConfiguration().apiUrl + '/Authenticate/authenAzureAD/', credentials));

            if (response.isAuthSuccessful) {
                this.accessToken = response.token;
                this.refreshToken = response.refreshToken;
                this._authenticated = true;

                const userProfile = {
                    id: response.userInfo.userId,
                    username: response.userInfo.userName,
                    name: response.userInfo.employeeName,
                    email: response.userInfo.email,
                    avatar: '',
                    status: '',
                    role: this.roleName[response.userInfo.roleId],
                    unit: response.userInfo.unitCode,
                    type: 'AD'
                };

                await this.csrfTokenService.fetchCsrfToken();  // Assuming fetchCsrfToken() is an async function

                this._userService.user = userProfile;
                return response;  // Resolve the Promise with the response
            } else {
                await await lastValueFrom(this._userService.clearDataUser());  // Again, assuming it returns an Observable
                throw new Error('Invalid credentials');
            }
        } catch (error) {
            console.error('Error during sign in:', error);
            throw error;  // Propagate errors by re-throwing them
        }
    }




    /**
     * Sign in using the access token
     */
    async signInUsingToken(): Promise<boolean> {

        const user = this._userService.user$;

        if (!user) {
            return false;
        }
        if (!user.username) {
            return false;
        }
        const tokenApi = {
            username: user.username,
            token: this.accessToken,
            refreshToken: this.refreshToken
        } as TokenApi;

        const response = await lastValueFrom(this._httpClient.post<AuthResponse>(this.configService.getConfiguration().apiUrl + '/Authenticate/refresh/', tokenApi));

        if (response.isAuthSuccessful) {
            this.accessToken = response.token;
            this.refreshToken = response.refreshToken;

            this._authenticated = true;

            const userProfile: User = {
                id: response.userInfo.userId,
                username: response.userInfo.userName,
                name: response.userInfo.employeeName,
                email: response.userInfo.email,
                avatar: '',
                status: '',
                role: this.roleName[response.userInfo.roleId],
                unit: response.userInfo.unitCode,
                type: user.type
            };

            this.csrfTokenService.fetchCsrfToken().then(() => {
                this._userService.user = userProfile;
                return of(response);
            });
        } else {
            this.clearToken();
            return false;
        }
    }

    /**
     * Sign out
     */
    async signOut(): Promise<Observable<any>> {
        console.log('signOut');
        await lastValueFrom(this._userService.clearDataUser()).then(
            () => of(true)
        );

        return of(false);
    }


    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { employeeTypeId: number; username: string; password: string }): Observable<any> {
        return this._httpClient.post<AuthResponse>(this.configService.getConfiguration().apiUrl + '/Authenticate/revoke/', credentials);
    }

    /**
     * Check the authentication status
     */
    async check(): Promise<boolean> {
        // Check if the user is logged in
        const res = await lastValueFrom(this._userService.getDataUser());

        if (!res) {
            // If no user data is returned, return false immediately
            return false;
        }

        if (this._authenticated) {
            // If already authenticated, return true
            return true;
        }

        // Check the access token availability
        if (!this.accessToken) {
            // If no access token is available, return false
            return false;
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            // If the access token has expired, return false
            return false;
        }

        // If the access token exists and it didn't expire, sign in using it
        return await this.signInUsingToken();
    }




    getProfile(userId: number): Observable<any> {

        return this._httpClient.get<ResponseUserInfo>(`${this.configService.getConfiguration().apiUrl}/User/Info/${userId}`).pipe();
    }


    getAntiforgeryToken(): Observable<any> {
        return this._httpClient.get('/AntiforgeryToken');
    }
    getBaseUrl(): string {
        return this.configService.getBaseApiUrl();
    }
}
